import {
  config,
  opened,
  digitalView,
  setOpen,
  openDigitalWelcome,
  minimizeDigital,
  webMaximized,
} from '../store/widget-store';

/**
 * 外部唤起入口 — 给宿主网页两种触发方式(隐藏迷你按钮 showButton=false 时尤其有用):
 *  1) window.DCSWidget.open(options?) / close() / toggle()  — JS API,固定契约;
 *  2) window 自定义事件 — 默认 dcs:widget:open / :close / :toggle;
 *     另支持后台配置的自定义 open 事件名(invokeEventName),config ready 后补挂。
 *     事件可通过 CustomEvent.detail 传与 open() 同形的 options。
 *
 * open 的 options 采用 JSON 配置对象,便于后续扩展(避免改签名):
 *   - maximized?: boolean  默认/省略 = 迷你聊天框(web 角落面板);true = 最大化(web 居中大窗)。
 *     digital 客服展开即全屏沉浸,maximized 对其不生效(始终全屏)。
 *
 * 唤起语义「直接展开对话视图」:web → 聊天面板;digital → 欢迎/沉浸视图。
 * 不依赖 showButton(只看 type / digitalView / opened),故按钮隐藏时仍可被唤起。
 *
 * 监听挂在顶层 window,跨 Shadow DOM 边界可达;生命周期 = 页面,无需 remove。
 * installed 标志 + boundEvents Set 防多次 bootstrap / 误嵌两份 script 时重复注册。
 */

export interface DCSOpenOptions {
  /** true=最大化展开(web 居中大窗);默认/省略=迷你聊天框(web 角落面板)。digital 不生效。 */
  maximized?: boolean;
}

const DEFAULT_OPEN_EVENT = 'dcs:widget:open';
const CLOSE_EVENT = 'dcs:widget:close';
const TOGGLE_EVENT = 'dcs:widget:toggle';

function openWidget(options?: DCSOpenOptions): void {
  const cfg = config.value;
  if (!cfg) return;
  if (cfg.type === 'digital') {
    // 数字人展开即全屏沉浸,无迷你/最大化之分;maximized 忽略
    if (digitalView.value === 'minimized') openDigitalWelcome();
    return;
  }
  // web:按 options 决定迷你(默认)/最大化,再打开面板。每次 open 显式设置,语义可预期。
  webMaximized.value = options?.maximized === true;
  setOpen(true);
}

function closeWidget(): void {
  const cfg = config.value;
  if (!cfg) return;
  if (cfg.type === 'digital') {
    minimizeDigital();
  } else {
    webMaximized.value = false;
    setOpen(false);
  }
}

function toggleWidget(): void {
  if (opened.value) closeWidget();
  else openWidget();
}

/** window 事件处理器:从 CustomEvent.detail 取与 open() 同形的 options(非对象则忽略)。 */
function handleOpenEvent(e: Event): void {
  const detail = (e as CustomEvent).detail;
  openWidget(detail && typeof detail === 'object' ? (detail as DCSOpenOptions) : undefined);
}

declare global {
  interface Window {
    DCSWidget?: {
      open: (options?: DCSOpenOptions) => void;
      close: () => void;
      toggle: () => void;
    };
  }
}

let installed = false;
const boundOpenEvents = new Set<string>();

function bindOpenEvent(name: string | null | undefined): void {
  if (!name || boundOpenEvents.has(name)) return;
  boundOpenEvents.add(name);
  window.addEventListener(name, handleOpenEvent);
}

/** 安装 window.DCSWidget API + 默认事件监听。幂等,放 bootstrap 公共路径调用一次。 */
export function installWidgetApi(): void {
  if (installed) return;
  installed = true;
  window.DCSWidget = {
    open: openWidget,
    close: closeWidget,
    toggle: toggleWidget,
  };
  bindOpenEvent(DEFAULT_OPEN_EVENT); // 默认 open 事件零等待可用(不依赖 config)
  window.addEventListener(CLOSE_EVENT, closeWidget);
  window.addEventListener(TOGGLE_EVENT, toggleWidget);
}

/**
 * config ready 后补挂后台配置的自定义 open 事件名(与默认名不同时才加,Set 去重)。
 * 用 effect 跟随 config.invokeEventName,避免「config 异步到达前事件名未知」的时序坑。
 */
export function ensureInvokeEvent(name: string | null | undefined): void {
  bindOpenEvent(name);
}
